import { TextField } from "@mui/material";
import { cn } from "@utils";
import { memo, useState } from "react";
import { ContentBlocksPreview } from "../ContentBlocksPreview";
import { messageAttachments } from "../attachments";
import type { ChatMessage } from "../types";
import { messageSkillContexts } from "../types";
import { getContentString } from "../utils";
import { CommandBar } from "./shared";
import {
  extractAssistantProcessBlock,
  extractContextFromPrompt,
  extractReferencedDocuments,
  extractUserQueryContent,
} from "./utils/utils";

// Simple preview component for system-generated messages
function HumanMessagePreview({
  preview,
  contextConfig,
}: {
  preview: string;
  contextConfig?: Map<string, any>;
}) {
  const hasContext = contextConfig && contextConfig.size > 0;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md transition-colors">
      {/* Context section */}
      {hasContext && (
        <div className="px-3 pt-2 pb-1 border-b border-blue-200/50">
          <div className="flex flex-wrap gap-1">
            {Array.from(contextConfig.entries()).map(([key, value]) => (
              <span
                key={key}
                className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-md border border-blue-200"
              >
                {key
                  .replace(/-/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                {value.complete ? " ✓" : " ⚠"}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Preview text section */}
      <div className="px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-2 h-2 ml-1 bg-blue-500 rounded-full"></div>
          <span className="text-sm text-blue-700 flex-shrink-0">{preview}</span>
        </div>
      </div>
    </div>
  );
}

function EditableContent({
  value,
  setValue,
  onSubmit,
}: {
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  onSubmit: () => void;
}) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <TextField
      hiddenLabel
      multiline
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      className="focus-visible:ring-0 border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none  disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
    />
  );
}

interface HumanMessageProps {
  message: Pick<ChatMessage, "content" | "id" | "additional_kwargs">;
}

export function arePropsEqual(
  prevProps: HumanMessageProps,
  nextProps: HumanMessageProps
): boolean {
  // Compare message by ID
  if (prevProps.message.id !== nextProps.message.id) {
    return false;
  }

  // Compare message content
  if (
    JSON.stringify(prevProps.message.content) !==
    JSON.stringify(nextProps.message.content)
  ) {
    return false;
  }

  return (
    JSON.stringify(prevProps.message.additional_kwargs?.attachments) ===
    JSON.stringify(nextProps.message.additional_kwargs?.attachments)
  );
}

export const HumanMessage = memo(function HumanMessage({
  message,
}: HumanMessageProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState("");
  const contentString = getContentString(message.content);

  // Check if content contains user-query tags and extract the content
  const userQueryContent = contentString
    ? extractUserQueryContent(contentString)
    : null;
  const displayContent =
    userQueryContent !== null ? userQueryContent : contentString;

  // Check if this is a system-generated message with context
  const contextData = contentString
    ? extractContextFromPrompt(contentString)
    : null;
  const isSystemGenerated = !!contextData;

  // Extract referenced documents and data keys
  const referencedBlocks = contentString
    ? extractReferencedDocuments(contentString)
    : [];
  const assistantProcessBlock = contentString
    ? extractAssistantProcessBlock(contentString)
    : null;
  const contentBlocks = assistantProcessBlock
    ? [...referencedBlocks, assistantProcessBlock]
    : referencedBlocks;
  const attachments = messageAttachments(
    message.additional_kwargs?.attachments
  );
  const skillContexts = messageSkillContexts(
    message.additional_kwargs?.skillContexts
  );
  const hasReferencedContent =
    contentBlocks.length > 0 ||
    attachments.length > 0 ||
    skillContexts.length > 0;

  return (
    <div
      className={cn(
        "group flex items-center gap-2",
        isEditing && "w-full max-w-xl"
      )}
    >
      <div className={cn("flex flex-col", isEditing && "w-full")}>
        {isEditing ? (
          <EditableContent
            value={value}
            setValue={setValue}
            onSubmit={() => {}}
          />
        ) : (
          <div className="flex flex-col gap-1">
            {hasReferencedContent && (
              <ContentBlocksPreview
                blocks={contentBlocks}
                attachments={attachments}
                skillContexts={skillContexts}
                onRemove={() => {}}
                className="pointer-events-none opacity-80"
                templatesConfig={{}}
                documents={[]}
                previewMode={true}
              />
            )}
            {/* Show preview for system-generated messages */}
            {isSystemGenerated ? (
              <HumanMessagePreview
                preview={contextData.preview || "System-generated prompt"}
                contextConfig={contextData.contextConfig}
              />
            ) : (
              <>
                {/* Render the display content (user-query content if present, otherwise full content) */}
                {displayContent ? (
                  <p className="bg-gray-200 w-fit rounded-md p-2 text-left text-gray-800 whitespace-pre-wrap m-0">
                    {displayContent}
                  </p>
                ) : null}
              </>
            )}
          </div>
        )}

        <div className="flex items-center self-start transition-opacity mt-2">
          <CommandBar
            isLoading={false}
            content={displayContent}
            isEditing={isEditing}
            setIsEditing={(c) => {
              if (c) {
                // For system-generated messages, show the full prompt when editing
                // For regular messages, show the display content
                const editContent = isSystemGenerated
                  ? contentString
                  : displayContent;
                setValue(editContent);
              }
              setIsEditing(c);
            }}
            onSubmitEdit={() => {}}
            isHumanMessage={true}
          />
        </div>
      </div>
    </div>
  );
}, arePropsEqual);
